/*
===============================================================================================================
                                 Copyright 2022-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useContext, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import { MerchantContext } from '@/contexts/merchant-context';
import { ChangesContext } from '@/contexts/changes-context';
import addresses from '@/utilities/addresses';
import { isEmpty } from '@/utilities/validation';
import AddressAutoComplete from '../address-auto-complete';

export default function AddressSearch({
  generateControl,
  addressFields,
}) {
  const merchantContext = useContext(MerchantContext);
  const {
    addressAutocompleteEnabled,
    valueModifiers,
    addressCountryCode,
  } = merchantContext.getFeature('addresses');
  const { addressFieldsHiddenIfEmpty } = merchantContext.getFeature('details');
  const { textDirection, translate, currentLocale } = useContext(LocaleContext);
  const { addChange, changes } = useContext(ChangesContext);
  const warehouse = merchantContext.getDefaultWarehouse();

  const [manualAddressEditing, setManualAddressEditing] = useState(false);

  const addressFieldsTotal = useMemo(() => {
    const inlineInputs = addressFields.filter((address) => address?.params?.inline).length;
    const newFields = addressFields.map((address) => {
      if (address?.params?.inline) {
        return { ...address, params: { ...address.params, inputWidth: Math.floor(100 / inlineInputs), inputMaxWidth: '128px' } };
      }
      return address;
    });
    return addresses.generateDropdownOptions(newFields, merchantContext.getCarrierId(), currentLocale, addressCountryCode);
  }, []);

  const shouldRenderAddressFields = useMemo(() => {
    if (addressFieldsHiddenIfEmpty) {
      return !isEmpty(merchantContext.getDefaultWarehouse()) || changes.find((change) => change.type === 'WAREHOUSE');
    }
    return !isEmpty(addressFieldsTotal) && generateControl;
  }, [addressFieldsTotal, warehouse, changes]);

  const initAutocomplete = (addressAutocomplete) => {
    addressAutocomplete.listen('manual', () => {
      setManualAddressEditing(true);
    });

    addressAutocomplete.listen('display', () => {
      setManualAddressEditing(false);
    });

    addressAutocomplete.listen('populate', (addressObj) => {
      addresses.generateAddressChange(addressObj, addChange, valueModifiers);
    });
  };

  // For carriers, need to make sure the warehouse have been added first before adding warehouse credentials
  const isAddressRequired = () => {
    const merchantCarriers = merchantContext.getMerchantCarriers();
    const { carriersWithPendingStatus = [] } = merchantContext.getFeature('carriers') || {};

    const isAddressEmpty = isEmpty(merchantContext.getDefaultWarehouse());
    const isCredentialsRequired = merchantCarriers?.some((carrier) => carriersWithPendingStatus.includes(carrier.carrierCompanyId));

    if (isAddressEmpty && !isEmpty(merchantCarriers) && isCredentialsRequired) return true;
    return false;
  };

  useEffect(() => {
    if (manualAddressEditing) {
      const firstAddressInput = addressFields[0].id;
      document.getElementById(firstAddressInput)?.focus();
    }
  }, [manualAddressEditing]);

  return (
    <div className="wrapper">
      {addressAutocompleteEnabled && (
        <div className="address-autocomplete">
          <AddressAutoComplete
            onLoad={initAutocomplete}
            placeholder={translate('details.addressSearchPlaceholder')}
            isAddressRequired={isAddressRequired()}
          />
        </div>
      )}
      {shouldRenderAddressFields && (
        <div className="address-fields-wrapper">
          <div className={`address-fields-row${textDirection === 'rtl' ? ' rtl' : ''}`}>
            {addressFieldsTotal.map((addressField) => generateControl(addressField))}
          </div>
        </div>
      )}
      <style jsx>
        {`
          .wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

          .address-fields-row {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
          }

          .address-fields-row.rtl {
            flex-direction: row-reverse
          }
        `}
      </style>
    </div>
  );
}

AddressSearch.defaultProps = {
  generateControl: null,
};

AddressSearch.propTypes = {
  generateControl: PropTypes.func,
  addressFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    params: PropTypes.shape({
      inline: PropTypes.bool,
      inputWidth: PropTypes.number,
    }),
  })).isRequired,
};
